import React, { useState } from 'react';
import { Box, Typography, IconButton, Grid, Chip } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

interface MultiDatePickerProps {
  selectedDates: string[]; // array of 'YYYY-MM-DD'
  onChange: (dates: string[]) => void;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const toKey = (date: Date) => date.toISOString().split('T')[0];

const MultiDatePicker: React.FC<MultiDatePickerProps> = ({ selectedDates, onChange }) => {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const toggleDate = (key: string) => {
    if (selectedDates.includes(key)) {
      onChange(selectedDates.filter(d => d !== key));
    } else {
      onChange([...selectedDates, key].sort());
    }
  };

  // Build calendar grid
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewYear, viewMonth, d));

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <IconButton size="small" onClick={prevMonth}><ChevronLeft /></IconButton>
        <Typography fontWeight={600}>{MONTHS[viewMonth]} {viewYear}</Typography>
        <IconButton size="small" onClick={nextMonth}><ChevronRight /></IconButton>
      </Box>

      {/* Day labels */}
      <Grid container columns={7} sx={{ mb: 0.5 }}>
        {DAYS.map(d => (
          <Grid key={d} size={1}>
            <Typography variant="caption" align="center" display="block" color="text.secondary" fontWeight={600}>
              {d}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* Calendar cells */}
      <Grid container columns={7}>
        {cells.map((date, i) => {
          if (!date) return <Grid key={`empty-${i}`} size={1} />;
          const key = toKey(date);
          const isSelected = selectedDates.includes(key);
          const isToday = key === toKey(today);
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          return (
            <Grid key={key} size={1}>
              <Box
                onClick={() => toggleDate(key)}
                sx={{
                  width: 34, height: 34, mx: 'auto', my: 0.3,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '50%', cursor: 'pointer',
                  bgcolor: isSelected ? 'primary.main' : 'transparent',
                  color: isSelected ? 'white' : isWeekend ? 'text.disabled' : 'text.primary',
                  border: isToday && !isSelected ? '1px solid' : 'none',
                  borderColor: 'primary.main',
                  fontWeight: isToday ? 700 : 400,
                  fontSize: 13,
                  '&:hover': { bgcolor: isSelected ? 'primary.dark' : 'action.hover' },
                }}
              >
                {date.getDate()}
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* Selected dates summary */}
      {selectedDates.length > 0 && (
        <Box mt={1.5} display="flex" flexWrap="wrap" gap={0.5}>
          {selectedDates.map(d => (
            <Chip
              key={d}
              label={new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              size="small"
              onDelete={() => toggleDate(d)}
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MultiDatePicker;
